import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  HostListener,
  inject,
  computed,
  ViewContainerRef,
  ComponentRef,
  effect,
  viewChild,
  signal,
  Injector,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { StepsModule } from 'primeng/steps';
import { ToastModule } from 'primeng/toast';
import { WizardNavigationComponent } from './components/wizard-navigation/wizard-navigation.component';
import { WizardStepComponent } from './components/wizard-step/wizard-step.component';
import { ActivityWizardStore } from './store/activity-wizard.store';
import { ActivityType, ActivityTypeEnum } from './models/wizard.models';
import { WizardConfigurationService } from './services/wizard-configuration.service';
import { WizardStepRegistryService } from './services/wizard-step-registry.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialog, ConfirmDialogModule } from 'primeng/confirmdialog';
import { ResizeService } from '@core/services/mobile/resize.service';
import { DialogService } from 'primeng/dynamicdialog';
import {
  GenericInfoUserComponent,
  InfoInterface,
} from './components/generic-info-user/generic-info-user.component';
import { SoggettiStore } from 'src/app/pages/features_shared/anagrafica-soggetti-utenze-condomini/soggetti/soggetti.store';
import { UtenzaStore } from 'src/app/pages/features_shared/anagrafica-soggetti-utenze-condomini/utenze/utenza.store';
import { CRMInitStore } from '../../../ticketing-sportello-online/CRMInit.store';
import { ServiziStore } from 'src/app/pages/features/servizi/lista-servizi/servizi.store';
import { mapServizioToWizardData } from 'src/app/pages/features/servizi/servizi-duplicazione.utils';

@Component({
  selector: 'app-activity-wizard',
  templateUrl: './activity-wizard.component.html',
  styleUrls: ['./activity-wizard.component.scss'],
  imports: [
    TranslateModule,
    ButtonModule,
    StepsModule,
    ToastModule,
    WizardNavigationComponent,
    ConfirmDialog,
    ConfirmDialogModule,
    GenericInfoUserComponent,
  ],
  providers: [ActivityWizardStore, DialogService],
})
export class ActivityWizardComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  // inject
  private readonly route = inject(ActivatedRoute);

  private readonly router = inject(Router);

  private readonly wizardStore = inject(ActivityWizardStore);

  private readonly configService = inject(WizardConfigurationService);

  private readonly stepRegistry = inject(WizardStepRegistryService);

  readonly resizeService = inject(ResizeService);

  readonly utenzaStore = inject(UtenzaStore);

  private readonly messageService = inject(MessageService);

  private readonly confirmationService = inject(ConfirmationService);

  readonly soggettiStore = inject(SoggettiStore);
  private readonly serviziStore = inject(ServiziStore);

  readonly stepContainer = viewChild('stepContainer', {
    read: ViewContainerRef,
  });

  private currentStepComponent: ComponentRef<WizardStepComponent> | null = null;

  // Signal per il cantiere selezionato da CRM
  private readonly _selectedCantiereFromCrm = signal<{
    codice: number;
    descrizione: string;
  } | null>(null);
  private readonly _selectedCantiereSoggetto = signal<{
    codice: number;
    descrizione: string;
  } | null>(null);

  CRMInitStore: any;

  cantieriList: { codice: number; descrizione: string }[] = [];

  // Computed signals from store
  currentStepIndex = this.wizardStore.currentStepIndex;

  availableSteps = this.wizardStore.availableSteps;

  wizardData = this.wizardStore.wizardData;

  isLoading = this.wizardStore.isLoading;

  isAttivita = this.router.url.includes('attivita');
  isServizi = this.router.url.includes('servizi');

  errors = this.wizardStore.errors;
  labelDialog = signal(
    this.isAttivita
      ? 'Vai alle attività'
      : this.isServizi
        ? 'Vai ai servizi'
        : 'Vai ai ticket',
  );
  titleDialog = signal(
    this.isAttivita ? 'Attività' : this.isServizi ? 'Servizi' : 'Ticket',
  );
  messageDialog = signal(
    this.isAttivita
      ? 'Attività creata con successo!'
      : this.isServizi
        ? 'Servizio creato con successo!'
        : 'Ticket creato con successo!',
  );
  createdId = signal<string>('');
  titleComponent = signal(
    this.isAttivita
      ? 'attivita.title_new_activity'
      : this.isServizi
        ? this.serviziStore.servizioDaDuplicare()
          ? `Duplica servizio: ${this.serviziStore.servizioDaDuplicare()?.nomeServizio || this.serviziStore.servizioDaDuplicare()?.nome || ''}`
          : 'Nuovo servizio'
        : 'Nuovo ticket',
  );

  private isWindowUnloading = false;

  @HostListener('window:beforeunload')
  onBeforeUnload() {
    this.isWindowUnloading = true;
  }

  // Current step component
  currentStep = computed(() => {
    const steps = this.availableSteps();
    const index = this.currentStepIndex();
    return steps[index] || null;
  });

  // Navigation state
  canGoBack = computed(() => {
    const currentIndex = this.currentStepIndex();
    const steps = this.availableSteps();

    // Controlla che ci sia almeno uno step non skippato precedente al corrente
    for (let i = currentIndex - 1; i >= 0; i--) {
      if (!steps[i].isSkipped) {
        return true;
      }
    }
    return false;
  });
  // Computed per i dati dell'utenza dal resource
  readonly utenzaCross = computed(() => this.utenzaStore.utenzaCrossValue());

  canGoNext = computed(() => {
    const steps = this.availableSteps();
    const currentIndex = this.currentStepIndex();
    return currentIndex < steps.length - 1;
  });

  isLastStep = computed(() => {
    const steps = this.availableSteps();
    const currentIndex = this.currentStepIndex();
    return currentIndex === steps.length - 1;
  });

  utenzaInfoData = signal<InfoInterface[]>([]);
  loadingUtenza = signal<boolean>(false);

  // life hooksn
  constructor(private readonly _injector: Injector) {
    effect(() => {
      if (this.currentStepComponent)
        this.currentStepComponent.instance.stepId();
    });
    effect(async () => {
      if (this.isServizi) {
        const activityTypeId = this.route.snapshot.params['activityType'];
        if (activityTypeId) {
          this.initializeWizard(activityTypeId, '', '', undefined);
        } else {
          this.router.navigate(['/lista-servizi']);
        }
        return;
      }

      let utenza;
      if (this.isAttivita) {
        utenza = this.utenzaStore.utenzaById();
      } else {
        utenza = this.utenzaCross();
      }
      if (utenza) {
        this.loadingUtenza.set(true);
        // Carica i dati del soggetto associato
        await this.soggettiStore.detailsSoggetto(utenza.idSoggetto);
        const soggetto = this.soggettiStore.soggettoById();

        const cantiereFromCrm = this._selectedCantiereFromCrm();

        const activityTypeId = this.route.snapshot.params['activityType'];
        const soggettoId =
          this.route.snapshot.params['idSoggetto'] ||
          this.soggettiStore.soggettoById()?.idSoggetto;
        const idUtenza =
          this.route.snapshot.params['idUtenza'] || utenza.idUtenza;

        if (activityTypeId && (soggettoId || idUtenza)) {
          this.initializeWizard(
            activityTypeId,
            soggettoId,
            idUtenza,
            this.isAttivita
              ? this.soggettiStore.soggettoById()?.idCantiere
              : this.utenzaStore.utenzaCrossValue()?.idCantiere,
          );
        } else {
          // Redirect back to nuova-attivita if missing params
          this.router.navigate(['/attivita/nuova-attivita', soggettoId || '']);
        }

        this.utenzaInfoData.set([
          {
            title: 'WIZARD.HEADER.CODICE_UTENZA',
            value:
              utenza.idUtenzaExt?.toString() ||
              utenza.idUtenza?.toString() ||
              '',
          },
          {
            title: 'WIZARD.HEADER.SOGGETTO',
            value:
              `${utenza.nomeSoggetto || ''} ${utenza.cognomeSoggetto || ''} ${utenza.ragioneSociale && (utenza.cognomeSoggetto || utenza.nomeSoggetto) ? '|' : ''} ${utenza.ragioneSociale || ''}`.trim(),
          },
          {
            title: 'WIZARD.HEADER.CF_PIVA',
            value: soggetto?.cf || soggetto?.piva || utenza?.cfPiva || '',
          },
          {
            title: this.isAttivita ? 'ID Origine' : 'Cantiere',
            value: this.isAttivita
              ? 'Manuale'
              : (cantiereFromCrm?.descrizione ?? utenza?.descrizioneCantiere) ||
                '',
          },
          {
            title: 'WIZARD.HEADER.INDIRIZZO',
            value: this.getDisplayAddress() || '',
          },
        ]);

        this.loadingUtenza.set(false);
      }
    });

    if (
      this.router.url.includes(ActivityTypeEnum.NUOVO_TICKET_RIFIUTI) ||
      this.router.url.includes(
        ActivityTypeEnum.NUOVO_TICKET_RITIRO_ON_DEMAND,
      ) ||
      this.router.url.includes(ActivityTypeEnum.NUOVO_TICKET_TARI)
    ) {
      this.CRMInitStore = _injector.get(CRMInitStore);
      this.cantieriList = this.CRMInitStore.cantieriVisibiliList();
    }
  }

  ngOnInit() {
    // Initialize step component registry
    this.stepRegistry.registerAllStepComponents();
    const idUtenza = this.route.snapshot.params['idUtenza'];
    this.utenzaStore.loadUtenzaCross(idUtenza);

    // Handle step navigation from URL
    this.route.queryParams.subscribe((queryParams) => {
      const step = queryParams['step'];

      if (step && !isNaN(+step)) {
        const stepIndex = +step;
        if (this.canNavigateToStep(stepIndex)) {
          this.wizardStore.navigateToStep(stepIndex);
          // Carica lo step da url
          setTimeout(() => {
            const steps = this.availableSteps();
            const targetStep = steps[stepIndex];
            if (targetStep && this.stepContainer()) {
              this.loadStepComponent(targetStep.id);
            }
          }, 100);
        }
      } else {
        // Se lo step non è indicato nell'url vai al primo step
        const currentIndex = this.currentStepIndex();
        if (currentIndex !== 0) {
          this.wizardStore.navigateToStep(0);
        }
      }
    });
  }

  ngAfterViewInit(): void {
    if (typeof window !== 'undefined') {
      (window as any).wizard = this;
    }

    // Load initial step component after view is initialized
    // Use setTimeout to ensure the wizard is fully initialized
    setTimeout(() => {
      this.tryLoadInitialStep();
    }, 100);
  }

  // methods
  private tryLoadInitialStep(): void {
    const step = this.currentStep();
    const steps = this.availableSteps();

    const stepContainer = this.stepContainer();
    if (step && stepContainer) {
      this.loadStepComponent(step.id);
    } else if (steps.length > 0 && stepContainer) {
      // Fallback: Carica il primo step in automatico
      this.loadStepComponent(steps[0].id);
    } else {
      // Retry after a longer delay
      setTimeout(() => {
        this.tryLoadInitialStep();
      }, 500);
    }
  }

  private async initializeWizard(
    activityTypeId: string,
    soggettoId: string,
    utenzaId: string,
    idCantiere?: number,
  ): Promise<void> {
    // Create activity type based on ID
    const activityType = this.createActivityTypeFromId(activityTypeId);

    if (activityType) {
      // Ensure soggetto data is loaded

      const currentSoggetto = this.soggettiStore.soggettoById();

      // Determina se stiamo arrivando con idSoggetto o idUtenza

      if (
        !this.isServizi &&
        ((soggettoId && !this.utenzaStore.utenzaById()) ||
          !this.utenzaStore.utenzaCrossValue())
      ) {
        // Se abbiamo l'ID dell'utenza, carica i dati dell'utenza
        try {
          //TODO VERIFICARE LOADUTENZA SU ATTIVITA
          // await this.utenzaStore.detailsUtenza(parseInt(soggettoId));
          await this.utenzaStore.loadUtenzaCross(Number.parseInt(utenzaId));

          const utenza =
            this.utenzaStore.utenzaById() ||
            this.utenzaStore.utenzaCrossValue();
          if (utenza) {
            // Se troviamo l'utenza, carichiamo anche i dati del soggetto associato
            await this.soggettiStore.detailsSoggetto(utenza.idSoggetto);
          }
        } catch (error) {}
      }

      // Preload components for this activity type for better performance
      try {
        await this.stepRegistry.preloadComponentsForActivityType(
          activityTypeId,
        );
      } catch (error) {
        // Continue with initialization even if preloading fails
      }

      this.wizardStore.initializeWizard(
        activityType,
        soggettoId ||
          (this.isServizi
            ? ''
            : this.utenzaStore.utenzaCrossValue()!.idSoggetto.toString()),
        utenzaId ||
          (this.isServizi
            ? ''
            : this.utenzaStore.utenzaCrossValue()!.idUtenza.toString()),
        this.isServizi
          ? undefined
          : this.utenzaStore.utenzaById()?.idUtenzaExt ||
              this.utenzaStore.utenzaCrossValue()?.idUtenzaExt,
        idCantiere,
      );

      if (this.isServizi) {
        const daDuplicare = this.serviziStore.servizioDaDuplicare();
        if (daDuplicare) {
          const nomeServizio =
            daDuplicare.nomeServizio || daDuplicare.nome || '';
          this.titleComponent.set(
            nomeServizio
              ? `Duplica servizio: ${nomeServizio}`
              : 'Duplica servizio',
          );
          const prefilled = mapServizioToWizardData(
            daDuplicare,
            activityTypeId as ActivityTypeEnum,
          );
          this.wizardStore.initializeServiziDuplication(prefilled);
        }
      }

      // Check for tipologiaData in router state (from CRM selection)
      const navigation = this.router.getCurrentNavigation();
      const state = navigation?.extras.state || history.state;

      if (state?.tipologiaData) {
        sessionStorage.setItem(
          'tipologiaData',
          JSON.stringify(state.tipologiaData),
        );
        this.wizardStore.updateStepData('tipologia', state?.tipologiaData);
      }

      // Check for cantiere in router state
      if (state?.cantiere) {
        this._selectedCantiereFromCrm.set(state.cantiere);
      }

      if (sessionStorage.getItem('tipologiaData')) {
        this.wizardStore.updateStepData(
          'tipologia',
          JSON.parse(sessionStorage.getItem('tipologiaData')!),
        );
      }

      // NUOVO_TICKET_TARI = 'nuovo-ticket-tari',
      // NUOVO_TICKET_RIFIUTI = 'nuovo-ticket-rifiuti',
      // NUOVO_TICKET_RITIRO_ON_DEMAND = 'nuovo-ticket-ritiro-on-demand',
      if (
        (this.soggettiStore.soggettoById()?.idCantiere?.toString() &&
          ActivityTypeEnum.NUOVO_TICKET_RIFIUTI == activityTypeId) ||
        ActivityTypeEnum.NUOVO_TICKET_TARI == activityTypeId ||
        ActivityTypeEnum.NUOVO_TICKET_RITIRO_ON_DEMAND == activityTypeId
      ) {
        this._selectedCantiereSoggetto.set({
          codice: this.soggettiStore.soggettoById()?.idCantiere!,
          descrizione:
            this.cantieriList.find(
              (c) => c.codice === this.soggettiStore.soggettoById()?.idCantiere,
            )?.descrizione || '',
        });
      }

      // Load the first step after initialization
      setTimeout(() => {
        this.loadFirstStep();
      }, 200);
    }
  }
  private loadFirstStep(): void {
    const steps = this.availableSteps();

    if (steps.length > 0 && this.stepContainer()) {
      const firstStep = steps[0];
      this.loadStepComponent(firstStep.id);
    } else {
      // Retry after a short delay
      setTimeout(() => {
        this.loadFirstStep();
      }, 300);
    }
  }

  private createActivityTypeFromId(
    activityTypeId: string,
  ): ActivityType | null {
    return this.configService.getActivityTypeById(activityTypeId);
  }

  // Enhanced navigation methods with comprehensive validation
  nextStep(): void {
    // Save current step data before validation
    this.saveCurrentStepData();

    // Validate the current step comprehensively
    const validationResult = this.validateCurrentStepWithFeedback();
    if (!validationResult.isValid) {
      this.showValidationResult(validationResult);
      return;
    }

    // Validation passed, continue with navigation

    // Mark current step as completed
    this.markCurrentStepCompleted();

    if (this.canGoNext()) {
      // Check for conditional navigation logic
      const nextIndex = this.getNextStepIndex();
      this.navigateToStep(nextIndex);
    }
  }

  previousStep(): void {
    // Save current step data before navigating back
    this.saveCurrentStepData();

    if (this.canGoBack()) {
      const currentIndex = this.currentStepIndex();
      const steps = this.availableSteps();

      // Cerca il priomo step non skippato andando a ritroso
      for (let i = currentIndex - 1; i >= 0; i--) {
        if (!steps[i].isSkipped) {
          this.navigateToStep(i);
          return;
        }
      }
    }
  }

  goToStep(index: number): void {
    const currentIndex = this.currentStepIndex();

    // Save current step data
    this.saveCurrentStepData();

    // If trying to go forward, validate current step and all intermediate steps
    if (index > currentIndex) {
      const validationResult = this.validateStepsUpTo(index);
      if (!validationResult.isValid) {
        this.showValidationResult(validationResult);
        return;
      }

      // Mark current step as completed if validation passed
      this.markCurrentStepCompleted();

      // Validation passed, continue with navigation
    }

    if (this.canNavigateToStep(index)) {
      this.navigateToStep(index);
    } else {
      // Provide specific feedback on why navigation is not allowed
      this.showNavigationBlockedMessage(index);
    }
  }

  private navigateToStep(index: number): void {
    this.wizardStore.navigateToStep(index);

    // Load the step component
    const steps = this.availableSteps();
    const step = steps[index];
    if (step && this.stepContainer()) {
      this.loadStepComponent(step.id);
    }

    // Update URL with step parameter for deep linking
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { step: index },
      queryParamsHandling: 'merge',
    });
  }

  canNavigateToStep(index: number): boolean {
    const steps = this.availableSteps();
    if (index < 0 || index >= steps.length) {
      return false;
    }

    const currentIndex = this.currentStepIndex();

    // Can always go to current step
    if (index === currentIndex) {
      return true;
    }

    // Can always go back to previous completed steps
    if (index < currentIndex) {
      return true;
    }

    // For forward navigation, check if all intermediate steps are completed and valid
    if (index > currentIndex) {
      // Check if we can navigate step by step to the target
      for (let i = currentIndex; i < index; i++) {
        const step = steps[i];
        if (!step || (!step.isCompleted && step.isRequired)) {
          return false;
        }
        if (step.isCompleted && !step.isValid) {
          return false;
        }
      }

      // Special case: can navigate to next step if current step is valid (even if not marked completed yet)
      if (index === currentIndex + 1) {
        const currentStep = steps[currentIndex];
        return this.isStepValidForNavigation(currentStep);
      }

      return true;
    }

    return false;
  }

  /**
   * Enhanced step validation for navigation purposes
   */
  private isStepValidForNavigation(step: any | null): boolean {
    if (!step) return false;

    // If step is already completed and valid, allow navigation
    if (step.isCompleted && step.isValid) {
      return true;
    }

    // If step is not required, allow navigation
    if (!step.isRequired) {
      return true;
    }

    // For required steps, check if current step component validates
    if (this.currentStepComponent) {
      return this.currentStepComponent.instance.validateStep();
    }

    return false;
  }

  // Save wizard data
  saveWizardData(): void {
    this.wizardStore.saveWizardData(this.utenzaStore.utenzaById()!.idUtenza);
  }

  // Handle step completion
  onStepCompleted(): void {
    // Mark current step as completed and valid
    this.wizardStore.markStepCompleted(this.currentStepIndex());

    // Update step visibility based on new data
    this.updateStepVisibility();
  }

  // Handle navigation events from child components
  onNavigationEvent(event: 'next' | 'previous' | 'save'): void {
    switch (event) {
      case 'next':
        this.nextStep();
        break;
      case 'previous':
        this.previousStep();
        break;
      case 'save':
        this.saveWizardDataWithValidation();
        break;
    }
  }

  // Cancel wizard and navigate back
  cancelWizard(): void {
    this.wizardStore.resetWizard();

    // Show confirmation dialog if there are unsaved changes
    const wizardData = this.wizardData();
    if (wizardData && Object.keys(wizardData).length > 2) {
      // More than just activityType and soggettoId
      this.navigateBack();
    } else {
      this.navigateBack();
    }
  }

  skipDeactivateGuard = false;
  navigateBack(save: boolean = false) {
    this.skipDeactivateGuard = true;
    const wizardData = this.wizardData();
    const soggettoId = wizardData?.soggettoId || '';
    if (
      this.route.snapshot.url.some((segment) =>
        segment.path.includes('nuovo-ticket'),
      )
    ) {
      return this.router.navigate(['/gestione-richieste-crm']).then(() => {
        this.skipDeactivateGuard = false;
      });
    }
    if (this.isServizi) {
      const isDuplicating = !!this.serviziStore.servizioDaDuplicare();
      this.serviziStore.clearServizioDaDuplicare();
      const routeServizi =
        save || isDuplicating
          ? '/lista-servizi'
          : '/lista-servizi/nuovo-servizio';
      return this.router.navigate([routeServizi]).then(() => {
        this.skipDeactivateGuard = false;
      });
    }
    let route = save ? '/attivita' : '/attivita/nuova-attivita/' + soggettoId;
    return this.router.navigate([route]).then(() => {
      this.skipDeactivateGuard = false;
    });
  }

  // Dynamic component loading
  private async loadStepComponent(stepId: string): Promise<void> {
    const stepContainer = this.stepContainer();
    if (!stepContainer) {
      return;
    }

    // Prevent loading the same component multiple times
    if (
      this.currentStepComponent &&
      this.currentStepComponent.instance.stepId() === stepId
    ) {
      return;
    }

    try {
      // Clear previous component
      this.clearCurrentStepComponent();

      // Load component class
      const componentClass = await this.stepRegistry.loadComponent(stepId);

      if (componentClass) {
        // Create component instance
        this.currentStepComponent =
          stepContainer.createComponent(componentClass);

        // Set component inputs
        this.currentStepComponent.instance.stepId.set(stepId);

        // Subscribe to component events
        this.currentStepComponent.instance.stepCompleted.subscribe(() => {
          this.onStepCompleted();
        });

        this.currentStepComponent.instance.stepDataChanged.subscribe(
          (data: any) => {
            this.handleStepDataChange(stepId, data);
          },
        );

        this.currentStepComponent.instance.validationChanged.subscribe(
          (isValid: boolean) => {
            this.wizardStore.updateStepValidation(
              this.currentStepIndex(),
              isValid,
            );
          },
        );

        // Activate the step
        this.currentStepComponent.instance.onStepActivated();
      } else {
      }
    } catch (error) {}
  }

  private clearCurrentStepComponent(): void {
    if (this.currentStepComponent) {
      // Deactivate the step before destroying
      this.currentStepComponent.instance.onStepDeactivated();
      this.currentStepComponent.destroy();
      this.currentStepComponent = null;
    }

    const stepContainer = this.stepContainer();
    if (stepContainer) {
      stepContainer.clear();
    }
  }

  ngOnDestroy(): void {
    this.clearCurrentStepComponent();
    if (!this.skipDeactivateGuard && !this.isWindowUnloading) {
      this.wizardStore.resetWizard();
    }
    if (this.isServizi && !this.isWindowUnloading) {
      this.serviziStore.clearServizioDaDuplicare();
    }
  }

  // Enhanced validation and helper methods

  /**
   * Validates the current step with detailed feedback including warnings
   */
  private validateCurrentStepWithFeedback(): {
    isValid: boolean;
    errors: string[];
    warnings?: string[];
  } {
    if (!this.currentStepComponent) {
      return { isValid: false, errors: ['Nessun step attivo da validare'] };
    }

    const stepValidation = this.currentStepComponent.instance.validateStep();
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!stepValidation) {
      const currentStep = this.currentStep();
      errors.push(
        `Compila correttamente tutti i dati in: "${currentStep?.title || 'questo step'}"`,
      );
    }

    // Additional validation can be added here in the future

    // Show warnings to user (but don't block navigation)
    if (warnings.length > 0) {
      this.showValidationWarnings(warnings);
    }

    return {
      isValid: stepValidation,
      errors,
      warnings,
    };
  }

  /**
   * Validates all steps up to a target index with cross-step validation
   */
  private validateStepsUpTo(targetIndex: number): {
    isValid: boolean;
    errors: string[];
    warnings?: string[];
  } {
    const steps = this.availableSteps();
    const errors: string[] = [];
    const warnings: string[] = [];
    const currentIndex = this.currentStepIndex();

    // Validate current step first
    const currentValidation = this.validateCurrentStepWithFeedback();
    if (!currentValidation.isValid) {
      errors.push(...currentValidation.errors);
    }
    if (currentValidation.warnings) {
      warnings.push(...currentValidation.warnings);
    }

    // Check all intermediate required steps are completed
    for (let i = currentIndex + 1; i < targetIndex; i++) {
      const step = steps[i];
      if (step && step.isRequired && !step.isCompleted) {
        errors.push(
          `Completa il passaggio "${step.title}" prima di continuare`,
        );
      }
      if (step && step.isCompleted && !step.isValid) {
        errors.push(`Correggi gli errori nel passaggio "${step.title}"`);
      }
    }

    // Additional cross-step validation can be added here in the future

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * Saves current step data if component is available
   */
  private saveCurrentStepData(): void {
    if (this.currentStepComponent) {
      try {
        this.currentStepComponent.instance.saveStepData();
      } catch (error) {}
    }
  }

  /**
   * Marks current step as completed in the store
   */
  private markCurrentStepCompleted(): void {
    const currentIndex = this.currentStepIndex();
    this.wizardStore.markStepCompleted(currentIndex);
  }

  /**
   * Shows validation errors to the user
   */
  private showValidationErrors(errors: string[]): void {
    errors.forEach((error) => {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore di Validazione',
        detail: error,
        life: 5000,
      });
    });
  }

  /**
   * Shows validation warnings to the user
   */
  private showValidationWarnings(warnings: string[]): void {
    warnings.forEach((warning) => {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: warning,
        life: 4000,
      });
    });
  }

  /**
   * Shows combined validation result
   */
  private showValidationResult(result: {
    isValid: boolean;
    errors: string[];
    warnings?: string[];
  }): void {
    result.errors.forEach((error) => {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore di Validazione',
        detail: error,
        life: 5000,
      });
    });

    if (result.warnings) {
      result.warnings.forEach((warning) => {
        this.messageService.add({
          severity: 'warn',
          summary: 'Attenzione',
          detail: warning,
          life: 4000,
        });
      });
    }
  }

  /**
   * Shows message when navigation is blocked
   */
  private showNavigationBlockedMessage(targetIndex: number): void {
    const steps = this.availableSteps();
    const targetStep = steps[targetIndex];
    const currentIndex = this.currentStepIndex();

    let message = 'Navigazione non consentita';

    if (targetIndex > currentIndex) {
      message = `Completa i passaggi precedenti prima di accedere a "${targetStep?.title || 'questo step'}"`;
    } else if (targetIndex < 0 || targetIndex >= steps.length) {
      message = 'Step non valido';
    }

    this.messageService.add({
      severity: 'warn',
      summary: 'Navigazione Bloccata',
      detail: message,
      life: 4000,
    });
  }

  /**
   * Gets detailed progress information
   */
  getProgressInfo(): {
    current: number;
    total: number;
    completed: number;
    percentage: number;
    canProceed: boolean;
    nextRequiredStep: string | null;
  } {
    const steps = this.availableSteps();
    const currentIndex = this.currentStepIndex();
    const completed = steps.filter((step) => step.isCompleted).length;
    const percentage =
      steps.length > 0 ? Math.round((completed / steps.length) * 100) : 0;

    // Find next required step that's not completed
    const nextRequiredStep = steps
      .slice(currentIndex + 1)
      .find((step) => step.isRequired && !step.isCompleted);

    const canProceed = this.canGoNext() || this.isLastStep();

    return {
      current: currentIndex + 1,
      total: steps.length,
      completed,
      percentage,
      canProceed,
      nextRequiredStep: nextRequiredStep?.title || null,
    };
  }

  /**
   * Validates entire wizard before final save with comprehensive checks
   */
  validateWizardForCompletion(): {
    canComplete: boolean;
    errors: string[];
    warnings: string[];
  } {
    const validation = this.wizardStore.validateForFinalSave();
    const steps = this.availableSteps();
    const warnings: string[] = [...(validation.warnings || [])];

    // Check for optional steps that might be skipped
    const skippedOptionalSteps = steps.filter(
      (step) => !step.isRequired && !step.isCompleted,
    );

    // if (skippedOptionalSteps.length > 0) {
    //   warnings.push(
    //     `Hai saltato ${skippedOptionalSteps.length} passaggi opzionali: ${skippedOptionalSteps.map(s => s.title).join(', ')
    //     }`
    //   );
    // }

    // Additional completion checks
    const requiredSteps = steps.filter((step) => step.isRequired);
    const completedRequiredSteps = requiredSteps.filter(
      (step) => step.isCompleted,
    );

    if (completedRequiredSteps.length < requiredSteps.length) {
      const missingSteps = requiredSteps
        .filter((step) => !step.isCompleted)
        .map((step) => step.title);

      validation.errors.push(
        `Passaggi obbligatori mancanti: ${missingSteps.join(', ')}`,
      );
    }

    return {
      canComplete: validation.isValid,
      errors: validation.errors,
      warnings,
    };
  }

  /**
   * Gets the next step index considering conditional logic
   */
  private getNextStepIndex(): number {
    const currentIndex = this.currentStepIndex();
    const steps = this.availableSteps();
    const currentStep = steps[currentIndex];
    const wizardData = this.wizardData();

    // Check if current step has next step override logic
    if (currentStep?.nextStepOverride && wizardData) {
      const overrideStepId = currentStep.nextStepOverride(wizardData);

      if (overrideStepId) {
        const overrideIndex = steps.findIndex(
          (step) => step.id === overrideStepId,
        );
        if (overrideIndex !== -1) {
          // Mark skipped steps as skipped
          this.markSkippedSteps(currentIndex + 1, overrideIndex);
          return overrideIndex;
        }
      }
    }

    // Default: go to next step, but skip any that should be skipped
    let nextIndex = currentIndex + 1;
    while (nextIndex < steps.length) {
      const nextStep = steps[nextIndex];
      if (
        nextStep?.skipCondition &&
        wizardData &&
        nextStep.skipCondition(wizardData)
      ) {
        // Mark step as skipped
        this.wizardStore.markStepSkipped(nextIndex);
        nextIndex++;
      } else {
        break;
      }
    }

    return nextIndex;
  }

  /**
   * Marks steps as skipped between two indices
   */
  private markSkippedSteps(startIndex: number, endIndex: number): void {
    for (let i = startIndex; i < endIndex; i++) {
      this.wizardStore.markStepSkipped(i);
    }
  }

  /**
   * Checks if a step should be skipped based on current wizard data
   */
  private shouldSkipStep(stepIndex: number): boolean {
    const steps = this.availableSteps();
    const step = steps[stepIndex];
    const wizardData = this.wizardData();

    if (!step?.skipCondition || !wizardData) {
      return false;
    }

    return step.skipCondition(wizardData);
  }

  /**
   * Updates step visibility based on conditional logic
   */
  private updateStepVisibility(): void {
    const wizardData = this.wizardData();
    if (wizardData) {
      this.wizardStore.updateStepVisibilityBasedOnData(wizardData);
    }
  }

  /**
   * Refresh steps with conditional logic
   */
  refreshSteps(): void {
    this.wizardStore.refreshStepsWithConditionalLogic();
  }

  /**
   * Handle step data changes with automatic reset of subsequent steps
   * @param stepId - ID of the step being modified
   * @param newData - New step data
   */
  private handleStepDataChange(stepId: string, newData: any): void {
    const steps = this.availableSteps();
    const stepIndex = steps.findIndex((step) => step.id === stepId);

    if (stepIndex === -1) {
      this.wizardStore.updateStepData(stepId, newData);
      return;
    }

    // Get previous data for comparison
    const wizardData = this.wizardData();
    const propertyName = stepId.replace(/-([a-z])/g, (_, letter) =>
      letter.toUpperCase(),
    );
    const previousData = wizardData ? (wizardData as any)[propertyName] : null;

    // Use the new method that handles reset automatically
    this.wizardStore.updateStepDataWithReset(stepId, newData, previousData);
  }

  /**
   * Enhanced save with comprehensive pre-save validation
   */

  saveWizardDataWithValidation(): void {
    // Save current step data first
    this.saveCurrentStepData();

    // Validate entire wizard with comprehensive checks
    const validation = this.validateWizardForCompletion();

    if (!validation.canComplete) {
      this.showValidationErrors(validation.errors);
      return;
    }

    // Show warnings if any (but allow save to continue)
    if (validation.warnings.length > 0) {
      this.showValidationWarnings(validation.warnings);
    }

    // Show confirmation dialog if there are warnings
    if (validation.warnings.length > 0) {
      const confirmMessage = `Ci sono ${validation.warnings.length} avvisi. Vuoi continuare con il salvataggio?`;
      if (!confirm(confirmMessage)) {
        return;
      }
    }

    // Proceed with save
    let idUtenza =
      this.utenzaStore.utenzaById()?.idUtenza ||
      this.utenzaStore.utenzaCrossValue()?.idUtenza;

    this.wizardStore.saveWizardData(idUtenza!).then((result) => {
      if (result.success) {
        if (result.codiceRitiro) {
          this.createdId.set(result.codiceRitiro);
        }
        this.successDialog();
      } else {
        // Error handling is already done in the store
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore durante il salvataggio. Controlla i dati e riprova.',
          life: 5000,
        });
      }
    });
  }
  successDialog() {
    this.confirmationService.confirm({
      key: 'success',
      header: this.titleDialog(),
      message: this.messageDialog(),

      rejectVisible: false,
    });
  }

  getDisplayAddress(): string {
    let isAttita = this.router.url.includes('attivita');
    let utenza;
    if (isAttita) {
      utenza = this.utenzaStore.utenzaById();
    } else {
      utenza = this.utenzaCross();
    }

    if (utenza) {
      return `${utenza.indirizzo}`.trim();
    }

    return '';
  }
}
